import React, { useState, useMemo, useCallback, useRef, useEffect, useContext } from 'react';
import { Dropdown } from 'primereact/dropdown';
import { MultiSelect } from 'primereact/multiselect';
import { Tooltip } from 'primereact/tooltip';
import { usePracticeAreasQuery } from 'hooks/queries/usePracticeAreasQuery';
import { expenseStatusOptions } from 'constants/dropdownOptions';
import TextSnippetWrapper from 'components/TextSnippetWrapper';
import debounce from 'lodash.debounce';
import TimezoneDatePicker from 'components/common/TimezoneDatePicker/TimezoneDatePicker';
import { UserDetailsContext } from 'context/userDetailsContext';
import { Button } from 'primereact/button';
import useFilterBreakpoint from 'hooks/useFilterBreakpoint';

const ExpensesFiltersV2 = ({ onFiltersChange, isFromCaseSpecificModule, isLeadSpecific }) => {
  const userContext = useContext(UserDetailsContext);
  // Filter state management
  const [searchKey, setSearchKey] = useState('');
  const [selectedDate, setSelectedDate] = useState('');
  const [selectedStatus, setSelectedStatus] = useState();
  const [selectedPractice, setSelectedPractice] = useState(null);

  // Use React Query hook for practice areas with dynamic query parameters
  const { practiceAreaList } = usePracticeAreasQuery({
    loadFullList: true,
    status: 'active',
    excludeFromLoading: false,
    sort_order: 'alphabet_ascending',
    pagination_token: '',
    pagination_direction: 'forward',
  });

  // Map practice areas to dropdown options
  const practiceAreaOptions = useMemo(() => {
    if (practiceAreaList && practiceAreaList.length > 0) {
      return practiceAreaList.map((val) => ({
        label: val.practice_area_name,
        value: val.practice_area_id,
      }));
    }
    return [];
  }, [practiceAreaList]);

  // Helper to notify parent of filter changes
  const notifyFilterChange = useCallback(
    (updates) => {
      if (onFiltersChange) {
        onFiltersChange({
          searchKey,
          selectedDate,
          selectedStatus,
          selectedPractice,
          ...updates,
        });
      }
    },
    [onFiltersChange, searchKey, selectedDate, selectedStatus, selectedPractice]
  );

  // Create debounced search function that uses current state
  const debouncedSearchRef = useRef(null);

  useEffect(() => {
    // Create debounced function that accesses current state via closure
    debouncedSearchRef.current = debounce((value) => {
      if (onFiltersChange) {
        onFiltersChange({
          searchKey: value,
          selectedDate,
          selectedStatus,
          selectedPractice,
        });
      }
    }, 500); // 500ms delay

    // Cleanup on unmount
    return () => {
      if (debouncedSearchRef.current) {
        debouncedSearchRef.current.cancel();
      }
    };
  }, [onFiltersChange, selectedDate, selectedStatus, selectedPractice]);

  // Handlers
  const onChangeSearch = (e) => {
    const value = e.target.value;
    setSearchKey(value);
    // Debounce the API call - will trigger after user stops typing for 500ms
    debouncedSearchRef.current(value);
  };

  const onSearchKeyDown = (e) => {
    const code = e.keyCode ? e.keyCode : e.which;
    if (code === 13) {
      // Enter key pressed - cancel debounce and trigger immediately
      debouncedSearchRef.current.cancel();
      notifyFilterChange({ searchKey: e.target.value });
    }
  };

  const onChangeCalendar = (e) => {
    setSelectedDate(e.value);
    notifyFilterChange({ selectedDate: e.value });
  };

  const onChangeStatus = (e) => {
    setSelectedStatus(e.value);
    notifyFilterChange({ selectedStatus: e.value });
  };

  const onChangePractice = (e) => {
    const newValue = [...e.value];
    setSelectedPractice(newValue);
    notifyFilterChange({ selectedPractice: newValue });
  };

  const refreshCase = () => {
    setSelectedStatus();
    setSearchKey('');
    setSelectedDate('');
    setSelectedPractice(null);
    // Notify parent of reset
    if (onFiltersChange) {
      onFiltersChange({
        searchKey: '',
        selectedDate: '',
        selectedStatus: undefined,
        selectedPractice: null,
      });
    }
  };
  const { showFilters, setShowFilters } = useFilterBreakpoint();

  return (
    <div className="d-flex flex-wrap align-items-center filter-wrap mb-3 p-lg-0 p-2 w-100 align-items-center justify-content-between">
      <div className="p-2 filter-wraper">
        <span className="p-input-icon-left input-shadow">
          <i className="icon-search F-size16"></i>
          <TextSnippetWrapper
            type="text"
            placeholder="Search"
            onChange={onChangeSearch}
            value={searchKey}
            onKeyDown={onSearchKeyDown}
            className="p-inputtext p-component input-search"
            autoComplete="off"
          />
        </span>
      </div>
      <div className="d-block d-lg-none w-100">
        <Button
          className="p-button p-component btn p-button-secondary outline w-100 filter-toggle-btn"
          type="button"
          onClick={() => setShowFilters((v) => !v)}
          aria-expanded={showFilters}
          aria-controls="expenses-filter-section"
          label="Filters"
        >
          <span
            style={{
              fontSize: 18,
              transition: 'transform 0.2s',
              display: 'inline-block',
              transform: showFilters ? 'rotate(180deg)' : 'rotate(0deg)',
            }}
          >
            <i className="fa fa-solid fa-angle-down"></i>
          </span>
        </Button>
      </div>
      <div
        className={`d-flex flex-wrap align-items-center p-2 expense-filter filter-wraper gap-2 filter-toggle-section filter-section${
          showFilters ? ' show' : ''
        }`}
        id="expenses-filter-section"
        aria-hidden={!showFilters}
      >
        <label className="text-bold black-600 filter-label">Filters:</label>
        <div className="d-flex flex-column mx-0 filter-wraper">
          <TimezoneDatePicker
            selectionMode="range"
            className="input-shadow filter-calender"
            id="icon"
            value={selectedDate}
            onChange={(value) => onChangeCalendar({ value })}
            showIcon
            placeholder="Expense Date"
            timezone={userContext?.userDetails?.timezone}
            isEndOfDay={true}
          />
        </div>
        <div className="d-flex flex-column filter-wraper">
          <Dropdown
            options={expenseStatusOptions}
            onChange={onChangeStatus}
            value={selectedStatus}
            placeholder="Invoice Status"
            className="searchable-dropdown"
          />
        </div>
        {!isFromCaseSpecificModule && !isLeadSpecific && (
          <div className="d-flex flex-column filter-wraper">
            <MultiSelect
              value={selectedPractice}
              options={practiceAreaOptions}
              onChange={onChangePractice}
              placeholder="Practice Area"
              maxSelectedLabels={1}
              display="chip"
              filter
              className="input-shadow w-100 F-size14 searchable-dropdown"
            />
          </div>
        )}

        <div className="d-flex flex-column mx-2 filter-wraper text-center">
          <div className="pointer ms-0">
            <i className="fas fa-sync-alt" onClick={refreshCase}></i>
            <Tooltip content="Reset Filter" position="top" target=".fa-sync-alt" showDelay={500} />
          </div>
        </div>
      </div>
    </div>
  );
};

export default ExpensesFiltersV2;
